import { useRef, useState } from 'react'

import { ImageUpload01Icon, Upload04Icon } from '@hugeicons/react'
import _ from 'lodash'
import { useController, useFormContext } from 'react-hook-form'
import toastr from 'toastr'

import { useDataProviderContext } from '@/shared/contexts/DataProviderContext'
import buildActiveStorageFilePayload from '@/shared/lib/file/build-active-storage-file-payload'
import { AppIcon } from '@/shared/ui/icons'
import { Button } from '@/shared/ui/Button'

type MissionImageEvidenceControlsProps = {
  isSubmitting: boolean
  isReadonly: boolean
  isCapacityFull: boolean
  submitLabel: string
}

export const MissionImageEvidenceControls = ({
  isSubmitting,
  isReadonly,
  isCapacityFull,
  submitLabel
}: MissionImageEvidenceControlsProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const { control, setValue } = useFormContext()
  const { httpClient } = useDataProviderContext()
  const {
    field: { value: mediaId, onChange }
  } = useController({
    name: 'media_id',
    control,
    rules: { required: true }
  })

  const hasMedia = Boolean(mediaId)
  const isBusy = isUploading || isSubmitting
  const isDisabled = isBusy || isCapacityFull || isReadonly

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploading(true)

    try {
      const filePayload = await buildActiveStorageFilePayload(file)
      const response = await httpClient.post('/user/medias', {
        record: { file: filePayload }
      })
      const record = _.get(response, 'data.record')

      setValue('media', record)
      onChange(record?.id ?? null)
      setPreviewUrl(URL.createObjectURL(file))
    } catch {
      toastr.error(
        'Não foi possível enviar a imagem. Tente novamente.',
        'Erro no upload'
      )
      onChange(null)
      setPreviewUrl(null)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <input
        id="mission-image-evidence-input"
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        className="sr-only"
        disabled={isDisabled}
        onChange={handleFileSelect}
      />

      <p className="m-0 flex items-center gap-2 text-sm font-semibold text-gray-900">
        <ImageUpload01Icon size={18} variant="duotone" className="text-primary" aria-hidden />
        {hasMedia ? 'Imagem selecionada' : 'Selecione a imagem da evidência'}
      </p>

      {previewUrl && (
        <img
          src={previewUrl}
          alt="Pré-visualização da evidência selecionada"
          className="max-h-56 w-full rounded-lg object-cover"
        />
      )}

      {!hasMedia && (
        <p className="m-0 text-xs text-gray-600">PNG, JPG ou GIF</p>
      )}

      {hasMedia ? (
        <Button
          type="submit"
          variant="primary"
          size="md"
          showTrailingIcon={false}
          className="w-full justify-center"
          disabled={isDisabled}
          leadingIcon={
            isBusy ? (
              <AppIcon name="spinner-third" />
            ) : (
              <Upload04Icon size={16} variant="stroke" />
            )
          }
        >
          {isSubmitting ? 'Enviando...' : submitLabel}
        </Button>
      ) : (
        <label htmlFor="mission-image-evidence-input" className="block w-full">
          <div className="pointer-events-none">
            <Button
              type="button"
              variant="primary"
              size="md"
              showTrailingIcon={false}
              className="w-full justify-center"
              disabled={isDisabled || isUploading}
              leadingIcon={
                isUploading ? (
                  <AppIcon name="spinner-third" />
                ) : (
                  <ImageUpload01Icon size={16} variant="stroke" />
                )
              }
            >
              {isUploading ? 'Enviando imagem...' : 'Selecionar evidência'}
            </Button>
          </div>
        </label>
      )}
    </div>
  )
}
